import { Component, OnInit, ElementRef, ViewChild, HostListener } from '@angular/core';
import { RegionService } from '../service/region.service';
import { Region, ImageCoordinate } from '../model/region';
import { forkJoin, Observable } from 'rxjs';
import { Quake, AgeColors } from '../model/quake';
import { EventQueryParameters } from '../model/event-query-parameters';
import { EventsService } from '../service/events.service';
import { TerminatorService } from '../service/terminator.service';
import { SettingsService } from '../service/settings.service';
import { ImageFilterService } from '../service/image-filter.service';
import { CacheService } from '../service/cache.service';
import { TranslateService } from '@ngx-translate/core';
import { strings as serbianString } from '../time-ago-language/sr-lat.js';
import { strings as spanishString } from '../time-ago-language/es.js';
import { strings as chroatString } from 'ngx-timeago/language-strings/hr';
import { strings as englishString } from 'ngx-timeago/language-strings/en';
import { TimeagoIntl } from 'ngx-timeago';
import screenfull from 'screenfull';

interface ImageQuakes {
  image: HTMLImageElement;
  events: Quake[];
}

interface CanvasBuffer {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
}
@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.css']
})
export class MapComponent implements OnInit {
  @ViewChild('imageCanvas', { static: true }) canvasRef: ElementRef;
  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      const nextPrev = event.key === 'ArrowRight' ? 1 : -1;
      this.getRegion(nextPrev);
    }
    if (event.key === 'h') {
      this.distractionFree = !this.distractionFree;
    }
    if (event.key === 'm') {
      this.showUI = !this.showUI;
    }
    if (event.key === 'f' && screenfull && screenfull.isEnabled) {
      screenfull.toggle();
    }
  }

  private ctx: CanvasRenderingContext2D;
  private TIMER_DURATION: number;
  private TIME_STEP = 1000;
  private OPACITY_STEP = 0.05;

  public canvas: HTMLCanvasElement;
  public region: Region;
  public remainTime: number;
  public progressWith = '0%';
  public latestEvents: Quake[];
  public lastMonthEvents: Quake[];
  public firstLoad = true;
  public AgeColorEnum = AgeColors;
  public showUI = false;
  public distractionFree = false;
  public colorLegend: string[];
  private FERegionsDict: any;
  public regionName: string;

  constructor(
    private regionService: RegionService,
    public eventsService: EventsService,
    public terminatorService: TerminatorService,
    public settingsService: SettingsService,
    private cacheService: CacheService,
    private imageFilterService: ImageFilterService,
    private translate: TranslateService,
    private intl: TimeagoIntl,
  ) {
    this.latestEvents = [];
    this.lastMonthEvents = [];
    this.region = new Region();
    this.updateTimeAgoLocalization();
    this.FERegionsDict = this.translate.instant('FE-regions');
  }

  public updateTimeAgoLocalization() {
    switch (this.settingsService.settings.language) {
      case 'sr':
        this.intl.strings = serbianString;
        break;
      case 'es':
        this.intl.strings = spanishString;
        break;
      default:
        this.intl.strings = englishString;
    }
    this.intl.changes.next();
  }

  public getRegionName(feKey: string): string {
    if (feKey in this.FERegionsDict) {
      return this.FERegionsDict[feKey];
    }
    return feKey;
  }

  public ngOnInit(): void {
    this.canvas = this.canvasRef.nativeElement;
    this.ctx = this.canvas.getContext('2d');
    this.getRegion();
    this.colorLegend = this.translate.instant('map.time_color_legend')
    //Thea eater: cacheTerminatorImage() > postprocessing() > mainLoop()
  }

  private mainLoop(): void {
    setInterval(() => {
      if (this.remainTime !== 0) {
        this.remainTime -= this.TIME_STEP;
      } else {
        this.remainTime = this.TIMER_DURATION;
        this.getRegion();
      }
      this.progressWith = (100 - this.remainTime / this.TIMER_DURATION * 100).toFixed() + '%';

      if (this.settingsService.settings.cycleTablePosition) {
        this.cycleTablePosition();
      }

    }, this.TIME_STEP);
  }

  private cycleTablePosition(): void {
    const steps = Math.floor(this.TIMER_DURATION / this.TIME_STEP);
    const currentStep = Math.floor(this.remainTime / this.TIME_STEP);
    if (Math.floor(steps * 0.25) === currentStep || Math.floor(steps * 0.75) === currentStep) {
      this.settingsService.settings.tablePosition = this.settingsService.settings.nextTablePosition().class;
    }
  }

  private mapTransitionAnimation(canvas: HTMLCanvasElement, opacity = 0): void {
    this.ctx.globalAlpha = opacity;
    this.ctx.drawImage(canvas, 0, 0);
    if (opacity < 1) {
      requestAnimationFrame(() => {
        this.mapTransitionAnimation(canvas, opacity += this.OPACITY_STEP);
      });
    }
  }

  private combineLayers(layers: Array<HTMLCanvasElement | HTMLImageElement>): HTMLCanvasElement {
    const c = this.canvasBufferFactory(this.canvas);
    layers.forEach((layer: HTMLImageElement | HTMLCanvasElement) => {
      if (layer) {
        c.ctx.drawImage(layer, 0, 0);
      }
    });
    this.drawLastMonthEvents(c.ctx);
    return c.canvas;
  }


  private getRegion(nextPrev = 1): void {
    this.region = this.regionService.getRegion(nextPrev);

    if (this.region.hideRegion === true) {
      return this.getRegion(nextPrev);
    }

    this.TIMER_DURATION = this.region.slideDuration;
    this.remainTime = this.TIMER_DURATION;

    this.getEventsAndImageObservable().subscribe((res: ImageQuakes) => {
      this.region.image = res.image;
      res.events = res.events as Quake[];
      this.region.events = res.events.filter((quake: Quake) =>
        this.region.quakeInRegion(quake) &&
        quake.magnitude >= this.region.minMagnitudeToShow
      );
      this.latestEvents = this.region.events.slice(0, this.settingsService.settings.animateHowMany);
      this.lastMonthEvents = this.eventsService.filterLastMonthEvents(this.region.events, this.latestEvents.length);
      this.regionName = this.translate.instant('map.regions.' + this.region.name);
      if (this.settingsService.settings.smartRegion === true && this.lastMonthEvents.length === 0) {
        return this.getRegion(nextPrev);
      }

      // Clear canvas content;
      if (this.firstLoad) {
        this.canvas.width = res.image.width;
        this.canvas.height = res.image.height;
      }
      const buffer = this.canvasBufferFactory(this.canvas);

      if (this.region.showTerminator) {
        this.cacheTerminatorImage(buffer);
      } else {
        this.postprocessing();
      }
    });
  }

  private createTerminatorImage(
    buffer: CanvasBuffer,
    image: HTMLImageElement
  ): HTMLCanvasElement {
    buffer.ctx.drawImage(image, 0, 0);
    buffer.ctx.restore();
    return buffer.canvas;
  }

  private cacheTerminatorImage(buffer: CanvasBuffer): void {
    const terminator = this.terminatorService.compute();
    let tp: ImageCoordinate[] = terminator.map(p => this.region.latLng2Pixel(p));
    tp = tp.filter(e => e !== null);
    if (tp.length) {
      buffer.ctx.beginPath();
      buffer.ctx.moveTo(tp[0].x, tp[0].y);
      buffer.ctx.fillStyle = '#00000000';
      tp.forEach(xy => {
        buffer.ctx.lineTo(xy.x, xy.y);
      });
      buffer.ctx.closePath();
      buffer.ctx.fill();
    }
    // clip to the path of the new polygon
    buffer.ctx.save();
    buffer.ctx.clip();
    const imageSource = 'assets/maps/earth_lights_4800.jpeg';
    let image = this.cacheService.get(imageSource);

    if (image) {
      const terminatorCanvas = this.createTerminatorImage(buffer, image)
      this.postprocessing(terminatorCanvas);
    } else {
      image = new Image();
      image.src = imageSource;
      image.onload = () => {
        this.cacheService.set(imageSource, image);
        const terminatorCanvas = this.createTerminatorImage(buffer, image)
        this.postprocessing(terminatorCanvas);
      };
    }
  }

  private canvasBufferFactory(parent: HTMLCanvasElement): CanvasBuffer {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = parent.width;
    canvas.height = parent.height;
    return {
      canvas,
      ctx
    }
  }

  private postprocessing(terminatorImage?: HTMLCanvasElement) {
    const eventsImageKey = this.region.name + '-events-image';
    const eventsImage = this.cacheService.get(eventsImageKey);
    const keepCached = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

    if (!eventsImage) {
      const buffer = this.canvasBufferFactory(this.canvas);

      const plot = this.region.events.map((quake: Quake) => this.region.latLng2Pixel(quake));
      buffer.ctx.lineWidth = 2;
      buffer.ctx.strokeStyle = this.settingsService.settings.getColor(AgeColors.YEAR);
      buffer.ctx.fillStyle = '#00000000';
      plot.forEach(xy => this.drawCircle(buffer.ctx, xy.x, xy.y, 5));

      // It would be vise to cache this image at this point.
      this.cacheService.set(eventsImageKey, buffer.canvas, keepCached);
      this.postprocessing(terminatorImage);
      return;
    }

    const effect = this.settingsService.settings.baseMapEffect;
    const transformedCanvas = this.imageFilterService[effect](this.region.image);
    if (terminatorImage) {
      terminatorImage = this.imageFilterService[effect](terminatorImage);
    }

    const layersCombined = this.combineLayers([transformedCanvas, terminatorImage, eventsImage]);
    this.mapTransitionAnimation(layersCombined);
    if (this.firstLoad) {
      this.firstLoad = false;
      this.mainLoop();
    }
  }

  private drawLastMonthEvents(ctx = this.ctx) {
    ctx.fillStyle = '#00000000';
    this.lastMonthEvents.forEach((quake: Quake) => {
      ctx.lineWidth = this.eventsService.getMagStrikeWidth(quake);
      const xy = this.region.latLng2Pixel(quake);
      if (xy === null) { return; }
      ctx.strokeStyle = this.eventsService.getColorFromAge(quake);
      this.drawCircle(ctx, xy.x, xy.y, this.eventsService.getMagnitudePixels(quake));
    });
  }

  private getEventsAndImageObservable(): Observable<ImageQuakes> {
    const options = new EventQueryParameters();
    options.minmag = 3;
    return forkJoin({
      events: this.eventsService.getEvents(options),
      image: this.region.loadImage()
    });
  }

  private drawCircle(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
    ctx.beginPath();
    ctx.arc(x, y, r, 0, 2 * Math.PI);
    ctx.stroke();
  }

  showLatest() {
    const t = this.eventsService.latestEvents.slice(0, 10).map(e => e.toString()).join("\n") || "No NEW events fetched in the last sync.";
    alert(t);
  }
}
